import { useEffect, useMemo } from 'react';
import { json } from '@remix-run/node';
import type { LoaderFunctionArgs, SerializeFrom } from '@remix-run/node';
import { useLoaderData, useLocation } from '@remix-run/react';
import { useSetAtom } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { getPreloadImageLink } from '~/modules/images';
import {
    createMetaDescription,
    createOGTags,
    fallbackOGSocialImage,
    getCanonicalHreflang,
    mergeMeta,
} from '~/modules/seo';
import { createMetaTitle } from '~/modules/seo';
import { createArticleSchema } from '~/modules/seo/schema/article-schema';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { ArticleDetails, ArticleIntro } from '@modules/article';
import {
    useAppDisplayModeContext,
    Container,
    useStyles,
} from '@archipro-design/aria';
import type {
    GetArticleDetailsQuery,
    GetArticleDetailsQueryVariables,
} from 'generated/graphql';
import { PermissionEnum } from 'generated/graphql';
import DetailModules from '@modules/page-builder/component/detail-article/detail-modules/DetailModules';
import { GetArticleDetailsDocument } from 'generated/graphql';
import { assertResponse } from '~/modules/root/graphql/assertResponse.server';
import { getModules } from '~/modules/page-builder/api/get-modules.server';
import DetailBannerSection from '@modules/page-builder/component/detail-article/detail-banner-section/DetailBannerSection';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { ProductTaggingContextProvider } from '~/modules/product-tagging/context';
import ArticleFooter from '@modules/article/component/article-footer/ArticleFooter';
import {
    getPermissions,
    useEditPermissions,
    usePermissions,
} from '@modules/user';
import getMemberRecentlyTagged from '~/modules/product-tagging/api/get-member-recently-tagged.server';
import { PaintPickerContextProvider as PaintPickerContextProviderV1 } from '@modules/paint-picker/context/PaintPickerContext';
import { PaintPickerContextProvider as PaintPickerContextProviderV2 } from '@modules/paint-picker-new/context/PaintPickerContext';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { unsafeVariant_DO_NOT_USE } from '~/utils/unsafeVariant';
import {
    FULL_COLUMN_WIDTH,
    FULL_COLUMN_WIDTH_MOBILE,
} from '~/modules/page-builder/config/column-constants';
import DownloadAction from '~/modules/page-builder/component/detail-article/detail-banner-section/actions/DownloadAction';
import { useRefreshOnRotate } from '@modules/root/util/use-refresh-on-rotate';
import { getBannerDimensions } from '~/utils/banner-utils';
import { getVideoBanner } from '@modules/page-builder/api/get-video-banner.server';
import { useSetEditMode } from '@modules/root/hook/use-set-edit-mode';
import { createWarningMessages } from '@modules/root/hook/use-warning-messages';
import makeArticleLink from '@modules/article/util/make-article-link';
import { useArticlePreviewRouteData } from '@modules/article/hook/use-article-preview-route-data';
import * as S from '@modules/article/component/article-details/ArticleDetails.style';
import { adsRequest } from '~/modules/adverts/api';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { useLocationMemo } from '~/modules/adverts/hook/use-location-memo';
import getModuleWithAds from '~/modules/article/util/get-module-with-ads';
import { makeArticleItemData } from '~/modules/adverts/utils/get-item-data.server';
import { noIndexCrossCountry } from '~/modules/seo/util/no-index-cross-country';
import { getBMPagePath } from '~/modules/root/util/get-bm-page-path';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { useFeature } from '~/modules/root';
import { getSession } from '~/modules/root/util/session.server';
import isApProfesional from '~/utils/is-ap-profesional';
import { useArticleGating } from '~/modules/article/hook/use-article-gating';
import { isTodayOrPastDate } from '@modules/date-time';
import { collectTextFromModules } from '@modules/page-builder/util/get-modules-text';
import { getMemberTagPermissions } from '~/modules/page-builder/util/get-tag-permissions.server';
import { ModulesContextProvider } from '~/modules/page-builder/context/modules-context';
import {
    getArticleDetails,
    getArticleMeta,
} from '~/modules/article/api/get-article-details.server';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';
import type { APHandle } from '@modules/root/type/handle';
import React from 'react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

const MAX_ADS = 2;
const AD_GAP = 3;

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        if (!data?.details?.title) {
            return [];
        }

        const { rootData } = getRouteData(matches);

        const title = createMetaTitle(
            props,
            [data?.meta?.Title || data.details.title],
            true,
            1,
            false
        );

        const hasIntroText = data?.details?.introText?.length > 0;
        const description = createMetaDescription([
            data?.localisation?.Description,
            hasIntroText
                ? data?.details?.introText
                : collectTextFromModules(data?.modules ?? [], 165),
        ]);

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            data?.banner?.libraryImage?.LibraryImageFile?.DetailBannerStandard
                ?.Desktop
        );

        // ToDo: Video schema
        const articleSchema = createArticleSchema(props);
        const FAQSchema = createFAQSchema(data.localisation?.FAQSchemas ?? []);
        const ldSchema = createLDSchema([articleSchema, FAQSchema]);

        const bannerImage = data?.banner.imageUrl;
        const preloadImageLinks =
            data?.deviceType === 'mobile'
                ? getPreloadImageLink(
                      undefined,
                      bannerImage,
                      getImagesSizes(378)
                  )
                : getPreloadImageLink(
                      bannerImage,
                      undefined,
                      getImagesSizes(1820)
                  );

        const noIndexCrossCountryMeta = !isApProfesional(
            data.details.professional.ID
        )
            ? noIndexCrossCountry(
                  data.details.professional.Country,
                  rootData?.localisation?.country,
                  data?.localisation
              )
            : [];

        return [
            { title },
            { name: 'description', content: description },
            ...(data?.noIndex ? [{ name: 'robots', content: 'noindex' }] : []),
            ...createOGTags({
                title,
                description,
                meta: props,
                facebookSocialImage:
                    data?.details?.facebookPreviewImage ?? fallbackSocialImage,
                type: 'article',
            }),
            ...getCanonicalHreflang(props, {
                forceAllRegions: data.details.byArchipro ?? false,
                professionalCountry: data.details.professional.Country,
            }),
            ...ldSchema,
            ...preloadImageLinks,
            ...noIndexCrossCountryMeta,
        ];
    }
);

export type ArticlePageLoader = SerializeFrom<typeof loader>;

export const loader = async (args: LoaderFunctionArgs) => {
    const { params, context, request } = args;
    const { user } = context;
    const isMobile = context.appDisplayMode === 'mobile';
    const link = makeArticleLink(params.article ?? '');
    const articleQuery = context.client.query<
        GetArticleDetailsQuery,
        GetArticleDetailsQueryVariables
    >({
        query: GetArticleDetailsDocument,
        variables: {
            link,
            liveOnly: true,
            editIntent: false,
        },
    });

    const [articleResult, memberRecentlyTagged, localisation, adsResponse] =
        await Promise.all([
            articleQuery,
            getMemberRecentlyTagged(args),
            getPageLocalisation(args),
            (async () => {
                // waterfall request to get the article data and feed it to the Ads API.
                // Avoid duplicated heavy ORM queries for the article data in the Ads BE.
                const articleResponse = await articleQuery;
                unsafeVariant_DO_NOT_USE(articleResponse);

                const projectItemData = await makeArticleItemData(
                    args,
                    articleResponse
                );

                return adsRequest({
                    type: 'ProjectPlacementGroup',
                    requestUrl: request.url,
                    item: projectItemData,
                    ...args.context,
                });
            })(),
        ]);

    unsafeVariant_DO_NOT_USE(articleResult);

    const article = await assertResponse(
        context,
        articleResult.data.Directory.Article,
        articleResult.error
    );

    const memberTagPermissions = getMemberTagPermissions(
        user,
        article.Professional?.ID || 0
    );
    const modules = getModules(
        article,
        memberTagPermissions,
        true,
        getArticleMeta(article),
        false
    );

    const noIndex = localisation?.NoIndex;

    // Professional team member who can edit article should also be able to see the warning
    const showPageNotLiveWarning =
        getPermissions(
            user,
            PermissionEnum.BmStaffView,
            article.Professional?.ID
        ) ||
        getPermissions(
            user,
            PermissionEnum.BmArticleEdit,
            article.Professional?.ID
        );

    let isAwaitingPublish = false;
    if (article.PublishDate && !isTodayOrPastDate(article.PublishDate)) {
        isAwaitingPublish = true;
    }

    // Create warning message for the WarningBanner
    const warningMessages = createWarningMessages(
        article.Professional?.Country || 'NZ',
        context.localisation.country,
        !article.IsLive ||
            article.Disable ||
            article.Professional?.Disable ||
            isAwaitingPublish,
        'article',
        false,
        {
            showPageNotLiveWarning: showPageNotLiveWarning,
            isAPStaff: getPermissions(user, PermissionEnum.BmStaffView),
        }
    );

    const ads = getProjectPlacements(adsResponse, isMobile);

    const session = await getSession(request);

    return json(
        {
            localisation,
            deviceType: context.device.type,
            articleID: article.ID,
            details: getArticleDetails(article),
            banner: getVideoBanner(
                article.ID,
                article.TopBanner,
                context.device.type
            ),
            modules,
            moduleMeta: getArticleMeta(article),
            memberPermissions: {
                canCreateTag: article?.Professional?.ID
                    ? getPermissions(
                          user,
                          PermissionEnum.BmProductTaggingCreate,
                          article.Professional.ID
                      )
                    : false,
                canDeleteTag: article?.Professional?.ID
                    ? getPermissions(
                          user,
                          PermissionEnum.BmProductTaggingDelete,
                          article.Professional.ID
                      )
                    : false,
                hasMarketingView: article?.Professional?.ID
                    ? getPermissions(user, PermissionEnum.BmMarketingView)
                    : false,
            },
            memberRecentlyTagged,
            noIndex,
            ads,
            meta: article.Meta,
            warningMessages,
        },
        {
            headers: await session.getHeaders(),
        }
    );
};

export const handle: APHandle = {
    hideContactArchiPro: true,
};

const ArticlePage = () => {
    const { desktop: isDesktop } = useAppDisplayModeContext();
    const { css } = useStyles({ isDesktop });
    useRefreshOnRotate();
    const location = useLocation();
    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory('Archizeen');
        return () => setCategory(null);
    }, [setCategory]);

    const {
        memberPermissions,
        memberRecentlyTagged,
        ads,
        articleID,
        moduleMeta,
    } = useLoaderData<typeof loader>();

    const { details, banner, modules } = useArticlePreviewRouteData();

    const setCountryData = useSetAtom(setCloneCountryStateAtom);

    useEffect(() => {
        setCountryData({
            urlSegment: details?.urlSegment,
            currentCountry: details?.professional?.Country,
            pageType: 'article',
            associatedPages: details?.associatedPages.edges.length
                ? details?.associatedPages
                : details?.professional?.professionalAssociatedPages,
        });
    }, [details, setCountryData]);

    const professionalDetails = {
        ID: details.professional.ID,
        Link: details.professional.link,
        CompanyLogo: details.professional.logo,
        LogoBackgroundColor: details.professional.logoBackgroundColor,
        Title: details.professional.Title,
        LibraryLocations: details.professional.libraryLocations,
        branchOptions: [],
        Website: details.professional.website,
        ArchiproEditorialAccount: details.professional.archiproEditorialAccount,
        CustomEnquiryMessage: details.professional.customEnquiryMessage,
        primaryCTA: details.professional.primaryCTA,
        AvailableForWork: details.professional.availableForWork,
    };

    const { canCreateTag, canDeleteTag, hasMarketingView } = memberPermissions;
    const { canEditArticles } = useEditPermissions(details.professional.ID);
    const canCopyLink = usePermissions(
        PermissionEnum.BmAnalyticsTrackingLinkShare,
        details.professional.ID
    );
    const articleEditPath = getBMPagePath(
        details.professional.URLSegment,
        'articles',
        details.urlSegment
    );
    const bannerSize = getImagesSizes({
        desktop: FULL_COLUMN_WIDTH,
        mobile: FULL_COLUMN_WIDTH_MOBILE,
    });
    const bannerDimensions = getBannerDimensions('article');
    const canDownloadBannerImage = isDesktop && hasMarketingView;

    useSetEditMode(articleEditPath, canEditArticles);

    const hideAd = location.state?.hideAd;

    // Keep the first value returned by loader to avoid changing when loader revalidate
    const adsMemoized = useLocationMemo(
        hideAd || typeof document === 'undefined' ? [] : ads
    );
    const modulesWithAds = useMemo(
        () => getModuleWithAds(modules, adsMemoized, MAX_ADS, AD_GAP),
        [modules, adsMemoized]
    );

    const { articleGatingEnabled, authBanner } = useArticleGating();

    const PickerContext = useFeature('resene_color_picker_2024')
        ? PaintPickerContextProviderV2
        : PaintPickerContextProviderV1;

    return (
        <div className={css(S.ArticlePageContainer)}>
            <Container>
                <PickerContext isEnabled={false}>
                    <ArticleDetails {...details} />
                    <DetailBannerSection
                        dimensions={bannerDimensions}
                        {...banner}
                        size={bannerSize}
                        // playsInlineVideoBanner={true}
                        // autoPlayVideoBanner={true}
                        // loopVideoBanner={false}
                        // hideVideoControls={false}
                        // showCaption={false}
                        actions={[
                            <DownloadAction
                                key="downloadAction"
                                canDownload={canDownloadBannerImage}
                                downloadLink={banner.downloadLink ?? ''}
                                libraryImageID={banner.libraryImage?.ID ?? 0}
                            />,
                        ]}
                    />
                    {details.introText && <ArticleIntro {...details} />}
                    <ProductTaggingContextProvider
                        canCreateTag={isDesktop && canCreateTag}
                        canDeleteTag={canDeleteTag}
                        recentlyTaggedProducts={memberRecentlyTagged.products}
                        recentlyTaggedProfessionals={
                            memberRecentlyTagged.professionals
                        }
                    >
                        <ModulesContextProvider meta={moduleMeta}>
                            <DetailModules
                                className={cn(
                                    'mt-14 md:mb-24 md:mt-24',
                                    articleGatingEnabled && 'md:mb-0'
                                )}
                                modules={modulesWithAds}
                                articleGatingEnabled={articleGatingEnabled}
                            />
                        </ModulesContextProvider>
                    </ProductTaggingContextProvider>
                    {!articleGatingEnabled && (
                        <DetailPageButtons
                            id={articleID}
                            editLink={articleEditPath}
                            shareableLink={`/article/${details.urlSegment}`}
                            canEdit={canEditArticles}
                            canCopyLink={canCopyLink}
                        />
                    )}
                </PickerContext>
            </Container>
            {!articleGatingEnabled && (
                <ArticleFooter
                    professional={professionalDetails}
                    articleID={articleID}
                    articleCategoryLink={details.category}
                />
            )}
            {authBanner}
        </div>
    );
};

export default ArticlePage;
